import { TPlan } from "@/components/Checkout/types.checkout";
import useUserPlanData from "@/components/Root/hooks/useUserPlanData";
import {
  BILLING_INTERVAL,
  DEFAULT_CURRENCY,
  PLAN_INTERVAL,
} from "@/data/constants";
import { useIsLocalisedPricing } from "@/hooks/useIsLocalisedPricing";
import { TBillingInterval } from "@/types/app";
import { RENEWAL_BILLING_REASONS } from "../constants.dashboard";
import { TYearlyUpsale } from "../types.dashboard";
import { isLedgerEligible } from "../utils/isLedgerEligible";
import { resolveYearlyUpsaleVariant } from "../utils/resolveYearlyUpsaleVariant";
import { useEstimateIntervalChangeQuery } from "@/components/shared/interval-switch/use-estimate-interval-change-query";
import { useInvoiceHistoryQuery } from "./queries/useInvoiceHistoryQuery";

interface YearlyUpsaleOptions {
  ownedPlanList: TPlan[];
  billingPeriod: TBillingInterval;
  isV1?: boolean;
}

/** Picks the yearly upsell to show: the ledger, the banner, or loading */
export function useYearlyUpsale({
  ownedPlanList,
  billingPeriod,
  isV1,
}: YearlyUpsaleOptions): TYearlyUpsale {
  const { data: userPlanData } = useUserPlanData();
  const { isLocalisedPricing, isResolved: isRegionResolved } =
    useIsLocalisedPricing();

  const planCurrency = ownedPlanList[0]?.pricing.currency ?? DEFAULT_CURRENCY;

  const isEligible = isLedgerEligible({
    billingPeriod,
    isV1,
    isLocalisedPricing,
  });
  // Localised users skip the ledger, so its queries wait for the region
  const isLedgerEnabled = isEligible && isRegionResolved;

  const { data: estimateData, isInitialLoading: isEstimating } =
    useEstimateIntervalChangeQuery({
      targetInterval: BILLING_INTERVAL.YEARLY,
      enabled: isLedgerEnabled,
      isV1,
      isLocalisedPricing,
      currency: planCurrency,
    });

  const subscriptionId = userPlanData?.subscription_id;
  const { data: invoiceHistory, isInitialLoading: isLoadingInvoices } =
    useInvoiceHistoryQuery({
      filters: {
        subscription_id: subscriptionId,
        billing_reason: RENEWAL_BILLING_REASONS,
        interval: PLAN_INTERVAL.MONTH,
      },
      enabled: isLedgerEnabled && !!subscriptionId,
    });

  const isAwaitingRegion = isEligible && !isRegionResolved;
  if (isAwaitingRegion || isEstimating || isLoadingInvoices) {
    return { kind: "loading" };
  }

  return resolveYearlyUpsaleVariant({
    billingPeriod,
    isV1,
    isLocalisedPricing,
    estimate: estimateData,
    invoices: invoiceHistory,
  });
}
